/* https://brandplatform.smartphoto.com/general/organisms/steps/ */
import {
	expressAddClass,
	expressEventListener,
	expressQuerySelector,
	expressQuerySelectorAll,
	expressRemoveClass,
	getParentWithClassName,
	hasClass,
	hide,
	isElementVisible,
	show,
} from "../../common/html";
import { getParameter, updateParameter } from "../../utils/querystring";
import { createCollapsable } from "../molecules/collapsable";
import { createPopup } from "../technical/popup-new";
import { ICustomPhotoSliderWithDivSettings } from "../technical/customslider";
import { initTooltip } from "../molecules/tooltip";
import { getPrice } from "../../services/order-service";
import { getCookie } from "../../utils/cookie";
import { IDataLayerProduct, pushEecDetailEvent } from "../technical/datalayer";
import { throttle } from "../../utils/debounce";
import { CarrouselItemType, createCarrousel, ICarrousel } from "../molecules/carrousel";
import { ProductEvent } from "../../common/events";
import { IPageLog, PageLogStyle } from "../molecules/page-log";
import { createAiMultipleFilters } from "../popups/ai-multiple-filters";
import { createAiPhotobookAssistantChoice } from "../popups/ai-photoboo-assistant-choice";
export interface IStepsComponent {
	initFormatImage: (sliderSettings: ICustomPhotoSliderWithDivSettings) => void;
	ctaButton: ICtaButton;
	mobileCtaButton: ICtaButton;
}

interface IStep {
	index: number;
	isLast: boolean;
	contentEl: HTMLElement;
	contents: IStepContent[];
	activate: (optionDescription: string) => void;
	hideContent: () => void;
	open: () => void;
	close: () => void;
	loadNextStep: (
		description: string,
		priceText?: string,
		strikedPriceText?: string,
		pricePerPieceText?: string,
		showFrom?: boolean,
		url?: string,
		attributeValueMa?: string,
		pvc?: string,
		uniqueSkuWithDesign?: string,
		aiPvc?: string,
		aiPhotobookAssistantPvc?: string,
		productCode?: string,
	) => boolean;
	hideError: () => void;
	setAndShowError: (message?: string) => void;
	selectedAttributeMaValue?: string;
	reset: () => void;
	hideLoading: () => void;
	showLoading: () => void;
}

interface IStepContent {
	id: string;
	visible: boolean;
	options?: IStepOption[];
	reset: () => void;
	select?: IDropDown;
	attributeValueMa: string;
	show: () => void;
	hide: () => void;
	openNextStepIfPossible: () => boolean;
	setId: (attributeValueMa: string) => void;
	getAttributeValueMaFromSelected: () => string;
}

export interface IStepOption {
	url?: string;
	formatImg?: string;
	select: () => void;
	deselect: () => void;
	customClick: () => void;
	disabled: boolean;
	showInfoIcon: boolean;
}

interface ICtaButton {
	disable: () => void;
	enable: (url: string, pvc: string, aiPhotobookAssistantProductCode: string) => void;
	setText: (buttonText: string, buttonTooltipText?: string) => void;
	resetText: () => void;
	isEnabled: () => boolean;

	buttonEl: HTMLButtonElement;
}

interface IDeliveryInformationUsp {
	setDeliveryDateAndPrice;
	disable: () => void;
	enable;
}

interface IPrice {
	setPrice: (price: string, strikedPrice: string, pricePerPiece: string, showFrom: boolean) => void;
}

export interface IPriceDetails {
	selectPvc: (pvc: string) => void;
}

interface IDropDown {
	selectFirst: () => void;
	reset: () => void;
	selectedOption: HTMLOptionElement;
	hasOnlyOneOption: () => boolean;
}

interface IVolumeRelatedPrices {
	update: (pvc: string, option: string, qtyPriceBreak: string, predefinedProductName: string) => void;
	showIcon: () => void;
	hideIcon: () => void;
}

export interface ICreateStepDependencies {
	readonly getStepContentAsync: (
		selectedAttributeValueMa: string[],
		stepIndex: number,
		optionGroup?: string,
		targetDeliveryDate?: string,
		theme?: string,
		predefinedProductName?: string,
	) => Promise<string>;
	readonly carrouselContainer?: HTMLElement;
	readonly priceDetails: IPriceDetails;
	readonly pricePopupListEl?: HTMLElement;
	readonly pageLogComponent: IPageLog;
}

let steps: IStep[] | undefined = undefined;
let ctaButton: ICtaButton;
let mobileCtaButton: ICtaButton;
let prices: IPrice[];
let deliveryInformationUsp: IDeliveryInformationUsp;
let formatImageSet: boolean;
let isLoading: boolean;

let volumeRelatedPrices: IVolumeRelatedPrices[];
let currentItem: IDataLayerProduct;

export function createSteps(containerEl: HTMLElement, dependencies: ICreateStepDependencies) {
	if (steps) return;
	steps = [];
	const { getStepContentAsync, carrouselContainer, priceDetails, pricePopupListEl, pageLogComponent } = dependencies;
	const stepEls = expressQuerySelectorAll<HTMLElement>(containerEl, ".technical-step");
	const ctaButtonEl = expressQuerySelector<HTMLButtonElement>(containerEl, ".technical-cta-component .a-button", true);
	const mobileCtaButtonEl = expressQuerySelector<HTMLButtonElement>(containerEl, ".technical-mobile-cta-button", true);
	const ctaTooltipButtonEl = expressQuerySelector<HTMLButtonElement>(
		containerEl,
		".technical-cta-component .m-tooltip__text",
		false,
	);
	const showMoreButtonEl = expressQuerySelector<HTMLElement>(containerEl, ".technical-showmore", false);
	const collapsibleEl = expressQuerySelector<HTMLElement>(document, ".technical-collapsables", false);
	const loadingMsg = containerEl.dataset.loadingmsg || "";
	const defaultPvc = containerEl.dataset.defaultpvc || "";
	const defaultProductCode = containerEl.dataset.defaultproductcode || "";
	const defaultProductName = containerEl.dataset.defaultproductname || "";
	const defaultFormatImage = containerEl.dataset.defaultformatimage || "";
	const defaultDeliveryDate = containerEl.dataset.defaultdeliverydate || "";
	const defaultPrice = parseFloat(containerEl.dataset.defaultprice || "0.0");
	const defaultOption = containerEl.dataset.defaultoption || "";
	const defaultQtyPriceBreak = containerEl.dataset.defaultquantitypricebreak || "-1";
	const predefinedProductName = containerEl.dataset.predefinedproductname || "";
	const priceContainerEls = expressQuerySelectorAll<HTMLElement>(document, ".technical-header-price-container"); // document because html duplication for mobile
	const theme = containerEl.dataset.theme || "";
	prices = [];
	volumeRelatedPrices = [];
	formatImageSet = false;
	isLoading = false;
	let carrousel: ICarrousel | undefined = undefined;
	const buttonDisabledClass = "a-button--disabled";

	const buildSelectedAttributeValueMa = (): string[] => {
		const toReturn = [];
		steps.forEach((step) => {
			if (step.selectedAttributeMaValue) toReturn.push(step.selectedAttributeMaValue);
		});
		return toReturn;
	};

	const createDataLayerProduct = (
		pvc: string,
		price: number,
		productCode: string,
		deliveryInfo: string,
		formatImg: string,
		pvcName: string,
	): IDataLayerProduct => {
		let categoryCode = containerEl.dataset.pc || "";
		if (categoryCode.indexOf("ProductCategory") >= 0) categoryCode = categoryCode.replace("ProductCategory.", "");
		return {
			currency: window.context.currency,
			categoryCode: categoryCode,
			categoryName: containerEl.dataset.pcname || "",
			pvc: pvc,
			price: price,
			productCode: productCode,
			deliveryInfo: deliveryInfo,
			parentCategories: containerEl.dataset.parentcategories || "",
			formatImageSrc: formatImg,
			pvcName: pvcName,
		} as IDataLayerProduct;
	};
	const resetSteps = (stepIndex: number) => {
		steps.forEach((step) => {
			if (step.index > stepIndex) step.reset();
			if (step.index > stepIndex + 1) step.close();
		});
	};

	const addFormatImages = (formatImg: string) => {
		if (carrousel) formatImageSet = carrousel.addAndSelectElement(formatImg);
	};

	const initFormatImage = () => {
		if (steps.length === 0) return;
		let formatImage = "";
		const selectedPvc = getParameter("pvc", document.location.href, false);
		if (steps[steps.length - 1].contents && steps[steps.length - 1].contents.length > 0) {
			const visibleContent = steps[steps.length - 1].contents.find((c) => c.visible);
			if (visibleContent && selectedPvc) {
				if (visibleContent.options && visibleContent.options.length > 0) {
					const selectedOption = visibleContent.options.find((o) => o.url && o.url.indexOf(selectedPvc) > 0);
					if (selectedOption) formatImage = selectedOption.formatImg;
				}
				if (visibleContent.select && visibleContent.select.selectedOption)
					formatImage = visibleContent.select.selectedOption.dataset.formatimage || "";
			}
		}
		if (formatImage) addFormatImages(formatImage);
	};

	const removeFormatImage = () => {
		if (formatImageSet && carrousel) {
			carrousel.removeFirstElementAndSelect(CarrouselItemType.FormatImage);
			formatImageSet = false;
		}
	};

	const removePackShotImage = () => {
		if (carrousel) carrousel.removeFirstElementAndSelect(CarrouselItemType.PackShot);
	};

	const preSelectStep = (stepContent: IStepContent, step: IStep, attributeValueMa: string, formatImg: string) => {
		stepContent.visible = true;
		stepContent.attributeValueMa = step.index === 0 ? "" : steps[step.index - 1].selectedAttributeMaValue;
		stepContent.setId(stepContent.attributeValueMa);
		step.selectedAttributeMaValue = step.isLast ? "" : attributeValueMa;
		if (formatImg) formatImageSet = true;
	};

	const initStep = (stepEl: HTMLElement, totalSteps) => {
		const index = parseInt(stepEl.dataset.index, 10) || 0;
		const collapsable = createCollapsable(stepEl, {
			contentSelector: ".technical-step-content",
			titleElSelector: ".technical-collapse-title",
			scrollIntoViewAfterOpen: true,
			openClass: "is-step-open",
		});

		// optional step => only has collapsable
		if (stepEl.classList.contains("m-step--optional")) return;
		// Sometimes we hit the code but the step is already initialised
		if (steps.length === totalSteps && steps.find((s) => s.index === index)) return;

		const stepContentContainerEl = expressQuerySelector<HTMLElement>(stepEl, ".technical-step-content", true);
		const errorEl = expressQuerySelector<HTMLElement>(stepContentContainerEl, ".technical-error-log", false);
		const loadingEl = expressQuerySelector<HTMLElement>(stepContentContainerEl, ".technical-loading", false);
		const defaultError = (errorEl && errorEl.innerText) || "";
		const stepContentEl = expressQuerySelector<HTMLElement>(stepEl, ".technical-step-content-element", false);
		const indicatorEl = expressQuerySelector<HTMLElement>(stepEl, ".technical-step-indicator", true);
		const subTitleEl = expressQuerySelector<HTMLElement>(stepEl, ".technical-step-subtitle", true);
		const activate = (optionDescription: string) => {
			if (hasClass(indicatorEl, "o-steps__step-container__indicator--active")) {
				expressRemoveClass(indicatorEl, "o-steps__step-container__indicator--active");
				expressAddClass(indicatorEl, "m-step__title-container__indicator--succes");
				indicatorEl.innerText = "";
			}
			if (optionDescription) subTitleEl.innerText = "(" + optionDescription + ")";
			else subTitleEl.innerText = "";
		};

		const deactivate = () => {
			if (hasClass(indicatorEl, "o-steps__step-container__indicator--active"))
				expressRemoveClass(indicatorEl, "o-steps__step-container__indicator--active");
			if (hasClass(indicatorEl, "m-step__title-container__indicator--succes"))
				expressRemoveClass(indicatorEl, "m-step__title-container__indicator--succes");
			subTitleEl.innerText = "";
			indicatorEl.innerText = step.index + 1 + "";
		};

		const hideContent = () => step.contents.forEach((c) => c.hide());

		const resetContent = () => step.contents.forEach((c) => c.reset());

		const open = () => {
			collapsable.open(false);
			expressAddClass(indicatorEl, "o-steps__step-container__indicator--active");
		};

		const close = () => {
			collapsable.close();
			expressRemoveClass(indicatorEl, "o-steps__step-container__indicator--active");
		};

		const hideError = () => errorEl && hide(errorEl);

		const setAndShowError = (message?: string) => {
			if (errorEl) {
				errorEl.innerText = message ? message : defaultError;
				show(errorEl);
			}
		};

		const hideLoading = () => loadingEl && hide(loadingEl);

		const showLoading = () => {
			if (loadingEl) {
				loadingEl.innerText = loadingMsg;
				show(loadingEl);
			}
		};

		const createId = (attributeValueMa: string): string => {
			let id = "";
			if (step.index === 0) return attributeValueMa;
			steps.forEach((s) => {
				if (s.index < step.index) id += s.selectedAttributeMaValue + "_";
			});
			id += attributeValueMa;
			return id;
		};

		const loadNextStep = (
			description: string,
			priceText?: string,
			strikedPriceText?: string,
			pricePerPieceText?: string,
			showFrom?: boolean,
			url?: string,
			attributeValueMa?: string,
			pvc?: string,
			uniqueSkuWithDesign?: string,
			aiPvc?: string,
			aiPhotobookAssistantProductCode?: string,
		) => {
			isLoading = true;
			let nextStepHasSelection = false;
			if (step.isLast) {
				ProductEvent("productSelection", { viewedUniqueSkuWithDesign: uniqueSkuWithDesign });
				if (priceText) {
					if (!showFrom) showFrom = false;
					prices.forEach((price) => price.setPrice(priceText, strikedPriceText, pricePerPieceText, showFrom));
				}
				if (url) {
					ctaButton.enable(url, aiPvc, aiPhotobookAssistantProductCode);
					mobileCtaButton.enable(url, aiPvc, aiPhotobookAssistantProductCode);
				}
				deliveryInformationUsp && deliveryInformationUsp.enable();
				step.activate(description);
				if (pvc && priceDetails && priceDetails.selectPvc) priceDetails.selectPvc(pvc);
				isLoading = false;
			} else {
				deliveryInformationUsp && deliveryInformationUsp.disable();
				ctaButton.resetText();
				ctaButton.disable();
				mobileCtaButton.resetText();
				mobileCtaButton.disable();
				step.activate(description);
				if (priceText) {
					if (!showFrom) showFrom = false;
					prices.forEach((price) => price.setPrice(priceText, strikedPriceText, pricePerPieceText, showFrom));
				}
				resetSteps(step.index);
				const nextStep = steps[step.index + 1];
				if (nextStep) {
					nextStep.hideContent();
					nextStep.hideError();
					let content = nextStep.contents.find((c) => c.id === createId(attributeValueMa));
					step.selectedAttributeMaValue = attributeValueMa;
					if (content) {
						content.show();
						isLoading = false;
						nextStepHasSelection = content.openNextStepIfPossible();
					} else if (attributeValueMa) {
						const selectedAttributeValuesMa = buildSelectedAttributeValueMa();
						nextStep.open();
						nextStep.showLoading();
						getStepContentAsync(
							selectedAttributeValuesMa,
							nextStep.index,
							getParameter("optiongroup", window.location.href),
							getParameter("tdd", window.location.href),
							theme,
							predefinedProductName,
						).then((res) => {
							if (res) {
								nextStep.contentEl.insertAdjacentHTML("afterbegin", res);
								const newContentEl = expressQuerySelector<HTMLElement>(
									nextStep.contentEl,
									".technical-step-content-element",
									true,
								);
								const stepOptions = expressQuerySelectorAll<HTMLElement>(newContentEl, ".technical-image-option");
								const stepDropDownEl = expressQuerySelector<HTMLSelectElement>(
									newContentEl,
									".technical-dropdown",
									false,
								);
								content = initStepContent(nextStep, newContentEl, stepOptions, stepDropDownEl, attributeValueMa);
								nextStep.hideLoading();
								content.show();
								nextStep.contents.push(content);
								isLoading = false;
								nextStepHasSelection = content.openNextStepIfPossible();
							} else {
								// Something went wrong => refresh page with error message
							}
						});
					}
				} else {
					isLoading = false;
				}
			}
			return nextStepHasSelection;
		};

		const reset = () => {
			step.selectedAttributeMaValue = "";
			hideContent();
			resetContent();
			setAndShowError(defaultError);
			deactivate();
		};

		const step: IStep = {
			index: index,
			isLast: index + 1 == totalSteps,
			contentEl: stepContentContainerEl,
			contents: [],
			activate: (optionDescription) => activate(optionDescription),
			hideContent,
			open,
			close,
			loadNextStep,
			hideError,
			setAndShowError: (message?: string) => setAndShowError(message),
			reset,
			hideLoading,
			showLoading,
		};

		if (stepContentEl) {
			const stepOptions = expressQuerySelectorAll<HTMLElement>(stepContentEl, ".technical-image-option");
			const stepDropDownEl = expressQuerySelector<HTMLSelectElement>(stepContentEl, ".technical-dropdown", false);
			step.contents.push(initStepContent(step, stepContentEl, stepOptions, stepDropDownEl));
		}
		steps.push(step);
	};

	const initStepContent = (
		step: IStep,
		stepContentEl: HTMLElement,
		stepOptions?: HTMLElement[],
		stepDropDownEl?: HTMLSelectElement,
		attributeValueMa?: string,
	): IStepContent => {
		const hideStepContent = () => {
			stepContent.visible = false;
			hide(stepContentEl);
		};

		const showStepContent = () => {
			stepContent.visible = true;
			show(stepContentEl);
			step.open();
		};

		const reset = () => {
			if (stepContent.options && stepContent.options.length > 0)
				stepContent.options.forEach((option) => option.deselect());
			if (stepContent.select) stepContent.select.reset();
		};

		const openNextStepIfPossible = (): boolean => {
			if (stepContent.options) {
				const enabledOptions = stepContent.options.filter((o) => !o.disabled);
				if (enabledOptions.length === 1) {
					enabledOptions[0].customClick();
					return true;
				}
			}

			if (stepContent.select && stepContent.select.hasOnlyOneOption()) {
				stepContent.select.selectFirst();
				return true;
			}

			return false;
		};

		const createId = (inputAttributeValueMa?: string): string => {
			let id = "";
			if (step.index === 0) return id;
			const previousStep = steps[step.index - 1].contents.find((c) => c.visible);
			id += previousStep.id;
			if (id) id += "_";
			id +=
				inputAttributeValueMa && inputAttributeValueMa !== ""
					? inputAttributeValueMa
					: attributeValueMa && attributeValueMa !== ""
						? attributeValueMa
						: previousStep.getAttributeValueMaFromSelected();
			return id;
		};

		const getAttributeValueMaFromSelected = (): string => {
			if (stepOptions && stepOptions.length > 0) {
				const selectedOption = stepOptions.find((o) => hasClass(o, "m-option-is-selected"));
				if (selectedOption) return selectedOption.dataset.attrvalma || "";
			}
			if (stepDropDownEl) return stepContent.select.selectedOption.value || "";
			return "";
		};

		const setId = (attributeValueMa: string) => (stepContent.id = createId(attributeValueMa));

		const stepContent: IStepContent = {
			id: createId(),
			visible: isElementVisible(stepContentEl),
			options: stepOptions && stepOptions.length > 0 ? [] : null,
			reset,
			attributeValueMa: attributeValueMa || "",
			show: showStepContent,
			hide: hideStepContent,
			openNextStepIfPossible,
			setId,
			getAttributeValueMaFromSelected,
		};

		if (stepOptions && stepOptions.length > 0) stepContent.options = initStepOptions(stepContent, stepOptions, step);
		if (stepDropDownEl) stepContent.select = initDropDown(stepContent, stepDropDownEl, step);

		return stepContent;
	};

	const initStepOptions = (stepContent: IStepContent, optionEls: HTMLElement[], step: IStep): IStepOption[] => {
		const options = [];
		optionEls.forEach((el) => options.push(initStepOption(stepContent, el, step)));
		return options;
	};

	const initStepOption = (stepContent: IStepContent, optionEl: HTMLElement, step: IStep): IStepOption => {
		const url = optionEl.dataset.url || "";
		const attributeValueMa = optionEl.dataset.attrvalma || "";
		const myPrice = parseFloat(optionEl.dataset.price || "0.0");
		const myPriceLabel = optionEl.dataset.pricelabel || "";
		const myStrikedPrice = parseFloat(optionEl.dataset.strikedprice || "0.0");
		const myStrikedPriceLabel = optionEl.dataset.strikedpricelabel || "";
		const myPricePerPiece = optionEl.dataset.priceperpiece || "";
		const showFrom = optionEl.dataset.showfrom === "true" || false;
		const formatImg = optionEl.dataset.formatimage || "";
		const fastDeliveryDateJs = optionEl.dataset.deliverydatejsFast || "";
		const fastDeliveryDate = optionEl.dataset.deliverydateFast || "";
		const fastDeliveryPrice = optionEl.dataset.deliverypriceFast || "";
		const cheapDeliveryDateJs = optionEl.dataset.deliverydatejsCheap || "";
		const cheapDeliveryDate = optionEl.dataset.deliverydateCheap || "";
		const cheapDeliveryPrice = optionEl.dataset.deliverypriceCheap || "";
		const buttonText = optionEl.dataset.buttontext || "";
		const mobileButtonText = optionEl.dataset.mobilebuttontext || "";
		const buttonTooltipText = optionEl.dataset.buttontooltiptext || "";
		const pagePrice = optionEl.dataset.pageprice || "";
		const pageMaxAmount = optionEl.dataset.pagemaxamount || "";
		const pageBaseAmount = optionEl.dataset.pagebaseamount || "";
		const pvc = optionEl.dataset.pvc || "";
		const uniqueSkuWithDesign = optionEl.dataset.uniqueskuwithdesign || "";
		const productCode = optionEl.dataset.productcode || "";
		const productName = optionEl.dataset.productname || "";
		const showInfoIcon = optionEl.dataset.showinfoicon === "true" || false;
		const showPriceUsp = optionEl.dataset.showpriceusp === "true" || false;
		const cheapestPvc = optionEl.dataset.cheapestpvc || "";
		const aiPvc = optionEl.dataset.aiPvc || "";
		const aiPhotobookAssistantEnabled = optionEl.dataset.enableaiphotobookassistant === "true" || false;
		const aiPhotobookAssistantProductCode = aiPhotobookAssistantEnabled ? productCode : "";
		const cheapestOption = optionEl.dataset.cheapestoption || "";
		const cheapestQtyPriceBreak = optionEl.dataset.cheapestquantitypricebreak || "-1";
		const defaultOptionUsp = optionEl.dataset.defaultoptionusp || "";
		const cheapestQtyPriceBreakNumber = parseInt(cheapestQtyPriceBreak, 10);
		const predefinedProductName = optionEl.dataset.predefinedproductname || "";
		const imgDescription = expressQuerySelector<HTMLElement>(optionEl, ".technical-image-description", true);
		const tempUnavailableEl = expressQuerySelector<HTMLElement>(optionEl, ".technical-temporary-unavailable", false);
		const disabled = hasClass(optionEl, "m-option--is-disabled") || tempUnavailableEl;
		const selected = hasClass(optionEl, "m-option-is-selected");

		const deSelect = () => expressRemoveClass(optionEl, "m-option-is-selected");

		const select = () => expressAddClass(optionEl, "m-option-is-selected");

		const onClick = () => {
			if (isLoading) return;
			if (stepContent.options) stepContent.reset();

			step.isLast && removePackShotImage();

			if (formatImg) {
				removeFormatImage();
				addFormatImages(formatImg);
			}

			const nextStepHasSelection = step.loadNextStep(
				imgDescription.innerText,
				myPriceLabel,
				myStrikedPrice > 0 ? myStrikedPriceLabel : "",
				myPricePerPiece,
				showFrom || cheapestQtyPriceBreakNumber > 1,
				url,
				attributeValueMa,
				pvc,
				uniqueSkuWithDesign,
				aiPvc,
				aiPhotobookAssistantProductCode,
			);
			option.select();
			// We need to check if a last step is selected. If that is the case we shouldn't reset the delivery times, prices, etc
			if (nextStepHasSelection) return;
			deliveryInformationUsp.setDeliveryDateAndPrice(
				fastDeliveryDate,
				fastDeliveryPrice,
				cheapDeliveryDate,
				cheapDeliveryPrice,
			);
			if (step.isLast) {
				updateDefaultOptionUsp(defaultOptionUsp);
			}
			if (buttonText) ctaButton.setText(buttonText, buttonTooltipText ? buttonTooltipText : "");
			else ctaButton.resetText();

			if (mobileButtonText) mobileCtaButton.setText(mobileButtonText);
			else mobileCtaButton.resetText();

			updatePageDetails(pagePrice, pageMaxAmount, pageBaseAmount);
			if (showInfoIcon) {
				volumeRelatedPrices.forEach((el) => {
					el.update(cheapestPvc, cheapestOption, cheapestQtyPriceBreak, predefinedProductName);
					el.showIcon();
				});
			} else {
				volumeRelatedPrices.forEach((el) => el.hideIcon());
			}
			togglePricePopupListEl(showPriceUsp);

			if (step.isLast)
				currentItem = createDataLayerProduct(pvc, myPrice, productCode, fastDeliveryDateJs, formatImg, productName);
		};

		if (selected) {
			preSelectStep(stepContent, step, attributeValueMa, formatImg);
			if (step.isLast) {
				currentItem = createDataLayerProduct(pvc, myPrice, productCode, fastDeliveryDateJs, formatImg, productName);
				ProductEvent("productSelection", { viewedUniqueSkuWithDesign: uniqueSkuWithDesign });
			}
		}

		const option = {
			url: step.isLast ? url : null,
			formatImg: formatImg,
			select,
			deselect: deSelect,
			customClick: onClick,
			disabled: disabled || false,
			showInfoIcon: showInfoIcon || false,
		} as IStepOption;
		!disabled && expressEventListener(optionEl, "click", onClick);

		return option;
	};
	const initCtaButtonEl = (buttonEl: HTMLButtonElement, tooltip?: HTMLElement): ICtaButton => {
		const completeStepsMsg = buttonEl.dataset.completestepsmsg || buttonEl.innerText;
		const defaultToolTipText = tooltip ? tooltip.innerText : "";

		const createCtaUrl = () => {
			let href = buttonEl.getAttribute("href");
			if (!href || href === "") return "";

			// Add the theme is it is not in the href but is in the page request url
			if (theme) {
				if (href.indexOf("theme", 0) < 0) {
					href += href.indexOf("?", 0) < 0 ? "?theme=" + theme : "&theme=" + theme;
				} else {
					updateParameter("theme", theme, href);
				}
			}
			return href;
		};
		const onClick = () => {
			if (hasClass(buttonEl, buttonDisabledClass)) return;
			// first check if ai photo upload is needed
			if (buttonEl.dataset.aiPvc) {
				const newAiUrl = updateParameter("pvc", buttonEl.dataset.aiPvc);
				if (document.location.href !== newAiUrl) history.replaceState({}, "", newAiUrl);
				expressAddClass(ctaButton.buttonEl, buttonDisabledClass);
				createAiMultipleFilters(
					{
						close: () => {
							expressRemoveClass(ctaButton.buttonEl, buttonDisabledClass);
						},
						error: (message: string) => {
							pageLogComponent.updateMessageAndStyle(PageLogStyle.Error, message);
						},
					},
					buttonEl.dataset.aiPvc,
					buttonEl.dataset.aiDesign,
					theme,
				);
				return;
			}

			// then check if the ai photobook assistant is needed
			if (buttonEl.dataset.aiPhotobookAssistantProductCode) {
				createAiPhotobookAssistantChoice(buttonEl.dataset.aiPhotobookAssistantProductCode, createCtaUrl());
				return;
			}

			// check normal flow
			const href = createCtaUrl();
			if (!href || href === "") return;

			let newUrl = document.location.href;
			const pvc = getParameter("pvc", href, false);
			if (pvc) {
				newUrl = updateParameter("pvc", pvc);
				if (document.location.href !== newUrl) history.replaceState({}, "", newUrl);
			}

			const url = new URL(newUrl);
			const relativePath = url.pathname + url.search;

			pushEecDetailEvent(relativePath, currentItem);
			document.location.href = href;
		};

		expressEventListener(
			buttonEl,
			"click",
			throttle(onClick, 1000, (e: Event) => {
				e.preventDefault();
			}),
		);
		const enable = (url: string, aiPvc: string, aiPhotobookAssistantProductCode: string) => {
			if (url) {
				buttonEl.setAttribute("href", url);
			}
			expressRemoveClass(buttonEl, buttonDisabledClass);
			buttonEl.dataset.aiPvc = aiPvc;
			buttonEl.dataset.aiPhotobookAssistantProductCode = aiPhotobookAssistantProductCode;
		};

		const disable = () => {
			buttonEl.setAttribute("href", "");
			expressAddClass(buttonEl, buttonDisabledClass);
		};

		const isEnabled = (): boolean => {
			return (
				buttonEl.getAttribute("href") && !hasClass(buttonEl, buttonDisabledClass) && !buttonEl.hasAttribute("disabled")
			);
		};

		const setText = (text: string, toolTipText?: string) => {
			if (text) buttonEl.innerText = text;
			if (tooltip && toolTipText) {
				buttonEl.title = toolTipText;
				tooltip.innerText = toolTipText;
			}
		};

		const resetText = () => {
			buttonEl.innerText = completeStepsMsg;
			if (tooltip) tooltip.innerText = defaultToolTipText;
		};

		return {
			enable,
			disable,
			setText,
			resetText,
			isEnabled,
			buttonEl,
		} as ICtaButton;
	};

	const initDeliveryInformationUsp = (): IDeliveryInformationUsp => {
		const deliveryInformationEl = expressQuerySelector<HTMLElement>(containerEl, ".technical-delivery-usp", false);
		if (deliveryInformationEl) {
			const linkEl = expressQuerySelector<HTMLElement>(deliveryInformationEl, ".technical-delivery-times-link", false);
			const headerLinkEls = expressQuerySelectorAll<HTMLElement>(document, ".technical-header-price-excl");
			const deliveryTableEl = expressQuerySelector<HTMLElement>(
				deliveryInformationEl,
				".technical-delivery-table",
				false,
			);

			const onClick = (e: Event) => {
				const link = e.currentTarget as HTMLElement;
				if (!link || (link && link.classList.contains("a-link--disabled"))) return; // disabled link not clickable
				const ctaButtonUrl = ctaButtonEl.getAttribute("href") ?? mobileCtaButtonEl.getAttribute("href");
				let pvc = ctaButtonUrl && getParameter("pvc", ctaButtonUrl, false);
				if (!pvc) pvc = defaultPvc;

				const toggleShowMore = (showmoreEl: HTMLElement, reset?: boolean) => {
					const arrow = expressQuerySelector<HTMLElement>(showmoreEl, ".technical-showmore-arrow", true);
					arrow.style.transform = `rotate(${reset || arrow.style.transform.indexOf("-180") >= 0 ? 0 : -180}deg)`;
					showmoreEl.nextElementSibling.classList.toggle("u-hide", reset ? true : undefined);
				};
				const toggleShowMoreEvent = (ev: Event, reset?: boolean) =>
					toggleShowMore((ev.target as HTMLElement).closest(".technical-delivery-collapsibles"), reset);
				const onBeforeOpen = (popupEl: HTMLElement) => {
					const collapsibles = expressQuerySelectorAll<HTMLElement>(popupEl, ".technical-delivery-collapsibles");
					// can't do this with collapsibles component => open close class on itself
					collapsibles.forEach((x) => {
						toggleShowMore(x, true); // reset dropdowns to closed
						x.addEventListener("click", toggleShowMoreEvent);
					});
				};
				const onClose = (popupEl: HTMLElement) => {
					if (link === linkEl) enable();

					const collapsibles = expressQuerySelectorAll<HTMLElement>(popupEl, ".technical-delivery-collapsibles");
					// can't do this with collapsibles component => open close class on itself
					collapsibles.forEach((x) => x.removeEventListener("click", toggleShowMoreEvent));
				};

				createPopup({
					dialogId: "deliverytimes" + pvc.replace("~", ""),
					view: "DeliveryTimesDialog",
					data: {
						pvc: pvc,
						removeCurrencySymbolFormUpperFunnelPages:
							window.context.removeCurrencySymbolFormUpperFunnelPages === "true",
					},
					onBeforeOpen: onBeforeOpen,
					onClose: onClose,
					onOpen: () => (link === linkEl ? disable() : void 0),
				}).then((popup) => {
					if (!popup.popup) return;
					popup.openAsync();
				});
			};
			const enable = () => linkEl && linkEl.classList.toggle("a-link--disabled", false);
			const disable = () => linkEl && linkEl.classList.toggle("a-link--disabled", true);
			const setDeliveryDateAndPrice = (
				fastDeliveryDate: string,
				fastDeliveryPrice: string,
				cheapDeliveryDate: string,
				cheapDeliveryPrice: string,
			) => {
				if (!deliveryTableEl) return;
				const fastestRowEl = expressQuerySelector<HTMLElement>(deliveryTableEl, ".technical-fastest-delivery", true);
				const cheapestRowEl = expressQuerySelector<HTMLElement>(deliveryTableEl, ".technical-cheapest-delivery", true);

				const fastestCelEls = expressQuerySelectorAll<HTMLElement>(fastestRowEl, "div");
				if (fastestCelEls[1]) fastestCelEls[1].innerText = fastDeliveryDate;
				const fastestCell2 = expressQuerySelector<HTMLParagraphElement>(fastestCelEls[2], "p", false);
				if (fastestCell2) fastestCell2.innerText = fastDeliveryPrice;

				if (fastDeliveryDate === cheapDeliveryDate && fastDeliveryPrice === cheapDeliveryPrice) {
					expressAddClass(cheapestRowEl, "u-hide");
				} else {
					const cheapestCelEls = expressQuerySelectorAll<HTMLElement>(cheapestRowEl, "div");
					if (cheapestCelEls[1]) cheapestCelEls[1].innerText = cheapDeliveryDate;
					const cheapestCell2 = expressQuerySelector<HTMLParagraphElement>(cheapestCelEls[2], "p", false);
					if (cheapestCell2) cheapestCell2.innerText = cheapDeliveryPrice;
					expressRemoveClass(cheapestRowEl, "u-hide");
				}
			};
			const disableLink = (el: HTMLElement) => {
				const ctaButtonUrl = ctaButtonEl.getAttribute("href") ?? mobileCtaButtonEl.getAttribute("href");
				const pvc = ctaButtonUrl && getParameter("pvc", ctaButtonUrl, false);
				if (!pvc && !defaultPvc) el.classList.add("a-link--disabled");
			};
			if (linkEl) {
				disableLink(linkEl);
				expressEventListener(linkEl, "click", onClick);
			}

			if (headerLinkEls)
				headerLinkEls.forEach((el) => {
					disableLink(el);
					expressEventListener(el, "click", onClick);
				});

			return {
				setDeliveryDateAndPrice,
				enable,
				disable,
			} as IDeliveryInformationUsp;
		}
	};

	const initPrice = (containerEl: HTMLElement): IPrice => {
		const fromEl = expressQuerySelector<HTMLElement>(containerEl, ".technical-from-tag", false);
		const priceEl = expressQuerySelector<HTMLElement>(containerEl, ".technical-price-tag", true);
		const strikedPriceEl = expressQuerySelector<HTMLElement>(containerEl, ".technical-strikedprice-tag", true);
		const pricePerPieceEl = expressQuerySelector<HTMLElement>(containerEl, ".technical-unitprice-tag", true);
		const infoIconEl = expressQuerySelector<HTMLElement>(containerEl, ".technical-price-info-icon", false);

		if (infoIconEl) {
			const volumeRelatedPrice = initShowVolumeRelatedPrices(infoIconEl);
			volumeRelatedPrices.push(volumeRelatedPrice);
		}

		const setPrice = (price: string, strikedPrice: string, pricePerPiece: string, showFrom: boolean) => {
			if (!priceEl) return;

			if (fromEl) {
				showFrom ? show(fromEl) : hide(fromEl);
			}
			priceEl.innerHTML = price.indexOf("-1") > -1 ? "" : price;
			if (pricePerPiece) {
				pricePerPieceEl.innerHTML = " " + pricePerPiece;
				pricePerPieceEl.classList.remove("u-hide");
			} else {
				pricePerPieceEl.classList.add("u-hide");
			}
			if (strikedPrice) {
				strikedPriceEl.innerHTML = " " + strikedPrice;
				strikedPriceEl.classList.remove("u-hide");
				containerEl.parentElement.classList.add("m-header--promotion");
				priceEl.setAttribute("class", priceEl.getAttribute("class").replace(" technical-u-color", " u-color"));
			} else {
				strikedPriceEl.classList.add("u-hide");
				containerEl.parentElement.classList.remove("m-header--promotion");
				priceEl.setAttribute("class", priceEl.getAttribute("class").replace(" u-color", " technical-u-color"));
			}
		};

		return {
			setPrice: (price, strikedPrice, pricePerPiece, showFrom) =>
				setPrice(price, strikedPrice, pricePerPiece, showFrom),
		} as IPrice;
	};

	const togglePricePopupListEl = (showPriceUsp: boolean) => {
		if (pricePopupListEl) {
			if (showPriceUsp) expressRemoveClass(pricePopupListEl, "u-hide");
			else expressAddClass(pricePopupListEl, "u-hide");
		}
	};

	const initDropDown = (stepContent: IStepContent, selectEl: HTMLSelectElement, step: IStep): IDropDown => {
		let selectedOption = selectEl.options[selectEl.selectedIndex];
		const selected = selectedOption.selected && !selectedOption.disabled;
		const options = [...selectEl.options];

		// the selected option is sometimes wrong when pushing the back button
		if (selectedOption.index === 0) {
			const optionWithSelectAttribute = options.find((o) => o.hasAttribute("selected"));
			if (optionWithSelectAttribute && optionWithSelectAttribute.index !== selectEl.selectedIndex) {
				selectEl.selectedIndex = optionWithSelectAttribute.index;
				selectedOption = optionWithSelectAttribute;
			}
		}
		let attributeValueMa = selectedOption.value || "";
		let formatImg = selectedOption.dataset.formatimage || "";
		let pvc = selectedOption.dataset.pvc || "";
		const uniqueSkuWithDesign = selectedOption.dataset.uniqueskuwithdesign || "";
		let myPrice = parseFloat(selectedOption.dataset.price || "0.0");
		let myStrikedPrice = parseFloat(selectedOption.dataset.strikedprice || "0.0");
		let productCode = selectedOption.dataset.productcode || "";
		let productName = selectedOption.dataset.productname || "";
		let fastDeliveryDateJs = selectedOption.dataset.deliverydatejsFast || "";
		let cheapDeliveryDateJs = selectedOption.dataset.deliverydatejsCheap || "";
		let defaultOptionUsp = selectedOption.dataset.defaultoptionusp || "";
		const onSelect = () => {
			if (isLoading) return;
			selectedOption = selectEl.options[selectEl.selectedIndex];
			attributeValueMa = selectedOption.value || "";
			const url = selectedOption.dataset.url || "";
			myPrice = parseFloat(selectedOption.dataset.price || "0.0");
			const myPriceLabel = selectedOption.dataset.pricelabel || "";
			myStrikedPrice = parseFloat(selectedOption.dataset.strikedprice || "0.0");
			const myStrikedPriceLabel = selectedOption.dataset.strikedpricelabel || "";
			const myPricePerPiece = selectedOption.dataset.priceperpiece || "";
			const showFrom = selectedOption.dataset.showfrom === "true" || false;
			const showPriceUsp = selectedOption.dataset.showpriceusp === "true" || false;
			const description = selectedOption.dataset.description || "";
			fastDeliveryDateJs = selectedOption.dataset.deliverydatejsFast || "";
			const fastDeliveryDate = selectedOption.dataset.deliverydateFast || "";
			const fastDeliveryPrice = selectedOption.dataset.deliverypriceFast || "";
			cheapDeliveryDateJs = selectedOption.dataset.deliverydatejsCheap || "";
			const cheapDeliveryDate = selectedOption.dataset.deliverydateCheap || "";
			const cheapDeliveryPrice = selectedOption.dataset.deliverypriceCheap || "";
			const buttonText = selectedOption.dataset.buttontext || "";
			const mobileButtonText = selectedOption.dataset.mobilebuttontext || "";
			const buttonToolTipText = selectedOption.dataset.buttontooltiptext || "";
			const pagePrice = selectedOption.dataset.pageprice || "";
			const pageMaxAmount = selectedOption.dataset.pagemaxamount || "";
			const pageBaseAmount = selectedOption.dataset.pagebaseamount || "";
			pvc = selectedOption.dataset.pvc || "";
			const aiPvc = selectedOption.dataset.aiPvc || "";
			const aiPhotobookAssistantEnabled = selectedOption.dataset.enableaiphotobookassistant === "true" || false;
			const aiPhotobookAssistantProductCode = aiPhotobookAssistantEnabled ? productCode : "";
			const uniqueSkuWithDesign = selectedOption.dataset.uniqueskuwithdesign || "";
			productCode = selectedOption.dataset.productcode || "";
			productName = selectedOption.dataset.productname || "";
			formatImg = selectedOption.dataset.formatimage || "";
			defaultOptionUsp = selectedOption.dataset.defaultoptionusp || "";
			const showInfoIcon = selectedOption.dataset.showinfoicon === "true" || false;
			const cheapestPvc = selectedOption.dataset.cheapestpvc || "";
			const cheapestOption = selectedOption.dataset.cheapestoption || "";
			const cheapestQtyPriceBreak = selectedOption.dataset.cheapestquantitypricebreak || "-1";
			const cheapestQtyPriceBreakNumber = parseInt(cheapestQtyPriceBreak, 10);
			const predefinedProductName = selectedOption.dataset.predefinedproductname || "";
			step.isLast && removePackShotImage();
			removeFormatImage();
			if (formatImg) addFormatImages(formatImg);
			deliveryInformationUsp.setDeliveryDateAndPrice(
				fastDeliveryDate,
				fastDeliveryPrice,
				cheapDeliveryDate,
				cheapDeliveryPrice,
			);

			if (step.isLast) {
				updateDefaultOptionUsp(defaultOptionUsp);
			}
			if (buttonText) ctaButton.setText(buttonText, buttonToolTipText);
			else ctaButton.resetText;
			if (mobileButtonText) mobileCtaButton.setText(mobileButtonText);
			else mobileCtaButton.resetText();
			step.loadNextStep(
				description,
				myPriceLabel,
				myStrikedPrice > 0 ? myStrikedPriceLabel : "",
				myPricePerPiece,
				showFrom || cheapestQtyPriceBreakNumber > 1,
				url,
				attributeValueMa,
				pvc,
				uniqueSkuWithDesign,
				aiPvc,
				aiPhotobookAssistantProductCode,
			);
			updatePageDetails(pagePrice, pageMaxAmount, pageBaseAmount);
			if (showInfoIcon) {
				volumeRelatedPrices.forEach((el) => {
					el.update(cheapestPvc, cheapestOption, cheapestQtyPriceBreak, predefinedProductName);
					el.showIcon();
				});
			} else {
				volumeRelatedPrices.forEach((el) => el.hideIcon());
			}
			if (step.isLast) {
				currentItem = createDataLayerProduct(pvc, myPrice, productCode, fastDeliveryDateJs, formatImg, productName);
			}
			togglePricePopupListEl(showPriceUsp);
		};

		const selectFirst = () => {
			if (selectEl.options.length === 1 || selectEl.options.length === 2) {
				const optionsWithValue = options.filter((o) => o.value !== "");
				if (optionsWithValue.length === 1) {
					const optionToSelect = options.find((o) => o.value !== "");
					selectEl.selectedIndex = optionToSelect.index;
					onSelect();
				}
			}
		};

		const reset = () => (selectEl.value = "");

		expressEventListener(selectEl, "change", onSelect);

		if (selected) {
			preSelectStep(stepContent, step, attributeValueMa, formatImg);
			if (step.isLast) {
				currentItem = createDataLayerProduct(pvc, myPrice, productCode, fastDeliveryDateJs, formatImg, productName);
				ProductEvent("productSelection", { viewedUniqueSkuWithDesign: uniqueSkuWithDesign });
			}
		}

		const hasOnlyOneOption = (): boolean => {
			if (selectEl.options.length === 1 || selectEl.options.length === 2) {
				const options = [...selectEl.options];
				const optionsWithValue = options.filter((o) => o.value !== "");
				return optionsWithValue.length === 1;
			}
			return false;
		};

		return {
			selectFirst,
			reset,
			selectedOption,
			hasOnlyOneOption,
		} as IDropDown;
	};

	const updatePageDetails = (pagePrice: string, pageMaxAmount: string, pageBaseAmount: string) => {
		const variableUl = expressQuerySelector<HTMLElement>(containerEl, ".technical-usps-variable-price", false);
		if (!pagePrice) {
			// Hide the section
			if (variableUl) variableUl.classList.add("u-hide");
		} else {
			if (variableUl) variableUl.classList.remove("u-hide");

			const basePageTagEl = expressQuerySelector<HTMLElement>(containerEl, ".technical-basepages-tag", false);
			const maxPagesTagEl = expressQuerySelector<HTMLElement>(containerEl, ".technical-maxpages-tag", false);
			const pagePriceTagEl = expressQuerySelector<HTMLElement>(containerEl, ".technical-unitprice-perpage-tag", false);
			const fromPagePriceTagEl = expressQuerySelector<HTMLElement>(
				containerEl,
				".technical-from-unitprice-perpage-tag",
				false,
			);
			if (basePageTagEl && pageBaseAmount) basePageTagEl.innerText = pageBaseAmount;
			if (maxPagesTagEl && pageMaxAmount) maxPagesTagEl.innerText = pageMaxAmount;
			pagePriceTagEl && fromPagePriceTagEl && fromPagePriceTagEl.classList.add("u-hide"); // update price => hide from label
			if (pagePriceTagEl) pagePriceTagEl.innerText = pagePrice;
		}
	};

	const updateDefaultOptionUsp = (usp: string) => {
		const defaultOptionEl = expressQuerySelector<HTMLLIElement>(document, "ul.technical-defaultoption", true);
		const defaultOptionListEl = expressQuerySelector<HTMLLIElement>(document, "ul.technical-defaultoption > li", true);
		if (!usp) expressAddClass(defaultOptionEl, "u-hide");
		else expressRemoveClass(defaultOptionEl, "u-hide");
		defaultOptionListEl.innerText = usp;
	};

	const initShowVolumeRelatedPrices = (infoIconEl: HTMLElement): IVolumeRelatedPrices => {
		if (!steps.length)
			currentItem = createDataLayerProduct(
				defaultPvc,
				defaultPrice,
				defaultProductCode,
				defaultDeliveryDate,
				defaultFormatImage,
				defaultProductName,
			);

		const infoTooltipEl = expressQuerySelector<HTMLElement>(infoIconEl.parentElement, ".technical-price-tooltip", true);

		const update = (pvc: string, option: string, qtyPriceBreak: string, predefinedProductName: string) => {
			infoTooltipEl.dataset.pvc = pvc;
			infoTooltipEl.dataset.quantitypricebreak = qtyPriceBreak;
			infoTooltipEl.dataset.option = option;
			infoTooltipEl.dataset.predefinedproductname = predefinedProductName;
		};

		const showIcon = () => {
			show(infoIconEl);
		};
		const hideIcon = () => {
			hide(infoIconEl);
		};

		if (defaultPvc && defaultQtyPriceBreak && defaultQtyPriceBreak > "1") {
			update(defaultPvc, defaultOption, defaultQtyPriceBreak, predefinedProductName);
			showIcon();
		}

		const onToolTipOpen = (infoTooltipEl) => {
			const forceQuantityBreak = infoTooltipEl.dataset.quantitypricebreak || "-1";
			const templateEl = expressQuerySelectorAll<HTMLElement>(infoTooltipEl, ".technical-price-template")[0];
			const containerEl = expressQuerySelector<HTMLElement>(infoTooltipEl, ".technical-price-container", true);
			const pvc = infoTooltipEl.dataset.pvc || "";
			const predefinedProductName = infoTooltipEl.dataset.predefinedproductname || "";
			const options = infoTooltipEl.dataset.option || undefined;
			const promoCode = getCookie("promo");
			let forceQuantityBreakInt: number;
			if (forceQuantityBreak) forceQuantityBreakInt = parseInt(forceQuantityBreak, 10);
			if (forceQuantityBreakInt <= 0) forceQuantityBreakInt = 1;
			containerEl.innerHTML = "";
			getPrice(
				pvc,
				forceQuantityBreakInt,
				0,
				options ? options.split(",") : undefined,
				promoCode,
				undefined,
				undefined,
				predefinedProductName,
			).then((res) => {
				if (res.PriceItems)
					res.PriceItems.forEach((item) => {
						// no item properties to set => skip
						if (!item.Description && !item.CalculationDescription && !item.ItemTotal) return;

						const newEl = templateEl.cloneNode(true) as HTMLElement;
						const description = expressQuerySelector<HTMLElement>(newEl, ".technical-price-tooltip-description", true);
						item.Description ? (description.innerHTML = item.Description) : description.remove();

						const calculation = expressQuerySelector<HTMLElement>(newEl, ".technical-price-tooltip-calculation", true);
						item.CalculationDescription ? (calculation.innerHTML = item.CalculationDescription) : calculation.remove();

						const result = expressQuerySelector<HTMLElement>(newEl, ".technical-price-tooltip-result", true);
						item.ItemTotal ? (result.innerHTML = item.ItemTotal) : result.remove();

						containerEl.insertAdjacentElement("beforeend", newEl);
					});
			});
		};

		initTooltip({
			buttonEl: infoIconEl,
			containerEl: infoTooltipEl,
			openClass: "is-tooltip-open",
			onOpen: onToolTipOpen,
		});

		return { update, showIcon, hideIcon } as IVolumeRelatedPrices;
	};

	if (showMoreButtonEl && collapsibleEl)
		expressEventListener(showMoreButtonEl, "click", () => {
			collapsibleEl.scrollIntoView({
				behavior: "smooth",
			});
		});

	stepEls.forEach((el) => initStep(el, stepEls.filter((x) => !x.classList.contains("m-step--optional")).length));
	deliveryInformationUsp = initDeliveryInformationUsp();
	ctaButton = initCtaButtonEl(ctaButtonEl, ctaTooltipButtonEl);
	mobileCtaButton = initCtaButtonEl(mobileCtaButtonEl);
	if (mobileCtaButtonEl) {
		const wobblerEl = expressQuerySelector(document, ".technical-wobbler-link", false);
		const mobileCtaButtonParentEl = getParentWithClassName(mobileCtaButtonEl, "o-steps__button-continue--wobbler");
		if (!wobblerEl && mobileCtaButtonParentEl) {
			expressRemoveClass(mobileCtaButtonParentEl as HTMLElement, "o-steps__button-continue--wobbler");
		}
	}
	priceContainerEls.forEach((priceEl) => prices.push(initPrice(priceEl)));

	carrousel = createCarrousel(carrouselContainer, { initFormatImage });

	return {
		ctaButton: ctaButton,
		mobileCtaButton: mobileCtaButton,
	} as IStepsComponent;
}
